import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { CelebrationItem, CelebrationPersona, EnrichedCelebration } from './celebration.types';
import { routeCelebrations, resolveSurface } from './celebration.router';
import {
  consumeCelebrationImmediate,
  readDeferredCelebrationIds,
  recordBlockingCelebrationShown,
  removeDeferredCelebrationId,
} from './celebration.session';
import { showCelebrationToasts } from './CelebrationToast';
import { CelebrationSheet } from './CelebrationSheet';
import { CelebrationModal } from './CelebrationModal';
import { CelebrationBatchModal } from './CelebrationBatchModal';

type Props = {
  persona: CelebrationPersona;
  items: CelebrationItem[];
  lockedAchievements?: Array<{ title: string; description: string; href: string }> | null;
  onDismiss: (id: string) => void | Promise<void>;
  renderIcon: (item: EnrichedCelebration) => React.ReactNode;
  progressHref: string;
  modalTestId?: string;
  enabled?: boolean;
};

type BlockingKind = 'sheet' | 'modal' | 'batch';

function itemKey(item: CelebrationItem): string {
  return item.id ?? item.code;
}

export const CelebrationHost: React.FC<Props> = ({
  persona,
  items,
  lockedAchievements,
  onDismiss,
  renderIcon,
  progressHref,
  modalTestId,
  enabled = true,
}) => {
  const location = useLocation();
  const surface = resolveSurface(location.pathname);
  const [handledIds, setHandledIds] = useState<Set<string>>(() => new Set());
  const [blockingQueue, setBlockingQueue] = useState<EnrichedCelebration[]>([]);
  const [batchItems, setBatchItems] = useState<EnrichedCelebration[] | null>(null);
  const [blockingKind, setBlockingKind] = useState<BlockingKind | null>(null);
  const [activeBlocking, setActiveBlocking] = useState<EnrichedCelebration | null>(null);

  const includeDeferred = surface === 'progress';

  const pendingItems = useMemo(() => {
    return items.filter((item) => !handledIds.has(itemKey(item)));
  }, [items, handledIds]);

  const markHandled = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setHandledIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }, []);

  const runPipeline = useCallback(() => {
    if (!enabled || pendingItems.length === 0) return;
    if (blockingKind !== null || activeBlocking !== null || batchItems !== null) return;

    const trigger = consumeCelebrationImmediate() ? 'immediate' : 'cold_load';
    const routed = routeCelebrations(
      pendingItems,
      {
        persona,
        surface,
        trigger,
        pathname: location.pathname,
      },
      { lockedAchievements, includeDeferred },
    );

    if (routed.toasts.length > 0) {
      const toastIds = routed.toasts.flatMap((t) => t.sourceIds ?? [t.id]);
      markHandled(toastIds);
      showCelebrationToasts(routed.toasts, persona, (id) => {
        void onDismiss(id);
      });
    }

    if (routed.batchModal && routed.batchModal.length > 0) {
      markHandled(routed.batchModal.map((i) => i.id));
      setBatchItems(routed.batchModal);
      setBlockingKind('batch');
      recordBlockingCelebrationShown();
      return;
    }

    const blocking = [...routed.sheets, ...routed.modals];
    if (blocking.length === 0) return;

    markHandled(blocking.map((i) => i.id));
    setBlockingQueue(blocking);
    const first = blocking[0];
    setActiveBlocking(first);
    setBlockingKind(first.tier === 2 ? 'sheet' : 'modal');
    recordBlockingCelebrationShown();
  }, [
    enabled,
    pendingItems,
    persona,
    surface,
    location.pathname,
    lockedAchievements,
    includeDeferred,
    onDismiss,
    markHandled,
    blockingKind,
    activeBlocking,
    batchItems,
  ]);

  useEffect(() => {
    runPipeline();
  }, [runPipeline]);

  const closeBlockingUi = useCallback(() => {
    setActiveBlocking(null);
    setBlockingKind(null);
    setBlockingQueue([]);
    setBatchItems(null);
  }, []);

  const advanceBlocking = useCallback(
    (id: string) => {
      removeDeferredCelebrationId(persona, id);
      markHandled([id]);

      setBlockingQueue((prev) => {
        const next = prev.filter((p) => p.id !== id);
        if (next.length === 0) {
          setActiveBlocking(null);
          setBlockingKind(null);
          return [];
        }
        const upcoming = next[0];
        setActiveBlocking(upcoming);
        setBlockingKind(upcoming.tier === 2 ? 'sheet' : 'modal');
        recordBlockingCelebrationShown();
        return next;
      });

      void onDismiss(id);
    },
    [markHandled, onDismiss, persona],
  );

  const closeBatch = useCallback(() => {
    if (!batchItems) return;
    const ids = batchItems.map((item) => item.id);
    markHandled(ids);
    for (const id of ids) {
      removeDeferredCelebrationId(persona, id);
      void onDismiss(id);
    }
    closeBlockingUi();
  }, [batchItems, closeBlockingUi, markHandled, onDismiss, persona]);

  if (!enabled && !activeBlocking && !batchItems) return null;

  return (
    <>
      <CelebrationSheet
        open={blockingKind === 'sheet' && Boolean(activeBlocking)}
        item={activeBlocking}
        persona={persona}
        icon={activeBlocking ? renderIcon(activeBlocking) : null}
        onClose={() => {
          if (activeBlocking) advanceBlocking(activeBlocking.id);
        }}
      />
      <CelebrationModal
        open={blockingKind === 'modal' && Boolean(activeBlocking)}
        item={activeBlocking}
        persona={persona}
        icon={activeBlocking ? renderIcon(activeBlocking) : null}
        testId={modalTestId}
        onClose={() => {
          if (activeBlocking) advanceBlocking(activeBlocking.id);
        }}
      />
      <CelebrationBatchModal
        open={blockingKind === 'batch' && Boolean(batchItems?.length)}
        items={batchItems ?? []}
        persona={persona}
        progressHref={progressHref}
        onClose={closeBatch}
      />
    </>
  );
};

export function useDeferredCelebrationCount(persona: CelebrationPersona): number {
  const location = useLocation();
  const [count, setCount] = useState(0);

  useEffect(() => {
    setCount(readDeferredCelebrationIds(persona).length);
  }, [persona, location.pathname]);

  return count;
}
